import { CotUtil } from "./CotUtil";
import { defaultApis, preDefinedGlobalFunctions } from "../../base/project/config/FunctionsConfig";
import { NodeType } from "./typed/CotTemplateResponseData";
import isBlank from "@sedan-utils/is-blank";
const offsetDistance = 300;
export class CotFlow {
    constructor(cotService, titleService) {
        this.cotService = cotService;
        this.titleService = titleService;
        this.nodeInitData = {};
        this.edgeNodeInitData = {};
        this.lineInitData = {};
    }
    async getTemplate(input, nodeInitData = {}, edgeNodeInitData = {}, lineInitData = {}) {
        /// cot web需要一些基础数据
        this.nodeInitData = nodeInitData;
        this.edgeNodeInitData = edgeNodeInitData;
        this.lineInitData = lineInitData;
        const flow = await this.cotService.getTemplate(input);
        const result = await this.transformTemplate(input, flow);
        return result;
    }
    async transformTemplate(input, flow) {
        const inputs = this.createInputNodes(flow.graph.nodes);
        const saasInputNodeIds = inputs.map(node => node.id);
        const extraSaasUIData = await this.getAppTitle(input, flow);
        const previewData = this.createPreviewData(flow.graph.nodes, input);
        const { edgeMap, nodeMap, outputs } = this.groupMapByTarget(flow);
        const { saasNodes, saasEdges, orderMap } = this.generateNodesAndEdges(edgeMap, nodeMap, CotUtil.getMaxIndexByFlow(flow));
        const flowGroup = this.createFlowGroup(flow.action_sequence, orderMap);
        return {
            inputs,
            outputs,
            flowGroup,
            previewData,
            extraSaasUIData,
            saasInputNodeIds,
            edges: saasEdges,
            otherNodes: saasNodes,
            nodes: [...inputs, ...saasNodes],
        };
    }
    generateNodesAndEdges(edgeMap, nodeMap, startNodeIndex) {
        const saasNodes = [];
        const saasEdges = [];
        const orderMap = new Map();
        for (let [targetId, sourceEdges] of edgeMap.entries()) {
            const targetNode = nodeMap.get(targetId);
            if (targetNode) {
                const description = targetNode.data.placeholder ?? "";
                const functionType = targetNode.data.functionType;
                let textAreaValue = this.getFunctionNameByEnterText(description, functionType);
                if (isBlank(textAreaValue)) {
                    // 如果匹配不到就默认为text并显示unsupported.
                    nodeMap.set(targetId, {
                        ...targetNode,
                        data: {
                            ...targetNode.data,
                            showWarning: true,
                        },
                    });
                    textAreaValue = description;
                }
                const { lines, edgeNode } = this.createSingleFlow(targetNode, textAreaValue, startNodeIndex, sourceEdges);
                startNodeIndex += sourceEdges.length + 2;
                saasNodes.push(edgeNode);
                saasNodes.push(targetNode);
                saasEdges.push(...lines);
                orderMap.set(targetId, {
                    edges: lines,
                    node: targetNode,
                    edgeNode: edgeNode,
                });
            }
        }
        return { saasNodes, saasEdges, orderMap };
    }
    groupMapByTarget(flow) {
        const outputs = [];
        const nodeMap = new Map();
        const edgeMap = new Map();
        const others = this.getNotInputNodes(flow.graph.nodes);
        others.forEach((node, index) => {
            const canvasNode = CotUtil.createNode(node, this.nodeInitData);
            nodeMap.set(canvasNode.id, canvasNode);
            outputs.push(CotUtil.createOutput(canvasNode, `preview-${index + 1}`));
        });
        // 把边按照target分组
        flow.graph.edges.forEach(edge => {
            const targetNodeId = `editor-${edge.target}`;
            edgeMap.set(targetNodeId, (edgeMap.get(targetNodeId) ?? []).concat(edge));
        });
        return {
            edgeMap,
            nodeMap,
            outputs,
        };
    }
    createSingleFlow(targetNode, textAreaValue, startNodeIndex, sourceEdges) {
        const targetId = targetNode.id;
        const newEdgeNumber = startNodeIndex++;
        const newEdgeId = `new-edge-${newEdgeNumber}`;
        const edges = sourceEdges.map(edge => ({
            textAreaValue,
            id: `new-line-${edge.id}`,
            source: `editor-${edge.source}`,
            edgeNodeId: newEdgeId,
        }));
        const flows = edges.map((edge, index) => ({
            sourceNodeId: edge.source,
            sourceLineId: edge.id,
            creationMethod: index === 0,
        }));
        // 创建EdgeNode
        const targetLineId = startNodeIndex++;
        const edgeNode = CotUtil.createEdgeNode({
            flows,
            targetId,
            id: newEdgeId,
            enterText: textAreaValue,
            position: {
                x: (targetNode.position?.x ?? 0) - offsetDistance,
                y: targetNode.position?.y ?? 0,
            },
            targetLineId: `new-line-${targetLineId}-${targetId}`,
        }, this.edgeNodeInitData);
        // targetLine
        edges.push({
            textAreaValue,
            id: `new-line-${targetLineId}-${targetId}`,
            source: newEdgeId,
            edgeNodeId: targetId,
        });
        const lines = edges.map(item => CotUtil.createLine(item, this.lineInitData));
        return { lines, edgeNode };
    }
    createInputNodes(nodes) {
        const inputs = this.getInputNodes(nodes);
        return inputs.map(node => CotUtil.createNode(node, this.nodeInitData));
    }
    createFlowGroup(sequence, orderMap) {
        const nodeOrders = sequence.filter(([type]) => type === "node").map(([, id]) => id);
        return nodeOrders
            .map(id => orderMap.get(`editor-${id}`) ?? orderMap.get(`group-${id}`) ?? null)
            .filter(item => isBlank(item) === false);
    }
    createPreviewData(nodes, input) {
        const inputs = this.getInputNodes(nodes);
        const others = this.getNotInputNodes(nodes);
        return {
            input: inputs.map(node => node.id),
            output: others.map(node => node.id),
            templateValue: input,
        };
    }
    /// 根据function_type转译边上的文字
    getFunctionNameByEnterText(description, functionType) {
        const allFunctions = preDefinedGlobalFunctions.concat(defaultApis);
        const functionName = (allFunctions.find(x => x.aliases?.includes(functionType)) || {}).label || functionType;
        if (!isBlank(functionType) && functionType !== "Text Generation") {
            if (functionName) {
                return `/${functionName}`;
            }
            return undefined;
        }
        return description;
    }
    /// 获取模版的title/subTitle
    async getAppTitle(input, flow) {
        if (isBlank(flow.title) === false && isBlank(flow.description) === false) {
            // 如果cot返回了title 则优先使用
            return {
                title: flow.title,
                subTitle: flow.description,
            };
        }
        const extraSaasUIData = await this.titleService.generateTitle(input);
        return extraSaasUIData;
    }
    /// 获取所有的input node
    getInputNodes(nodes) {
        return nodes.filter(node => node.type === NodeType.USERINPUT);
    }
    /// 获取所有的非input node
    getNotInputNodes(nodes) {
        return nodes.filter(node => node.type !== NodeType.CONNECTION && node.type !== NodeType.INTERACTION && node.type !== NodeType.USERINPUT);
    }
}
